import { ReactElement } from 'react'

import queryString from 'query-string'
import { Navigate, Outlet, OutletProps, useLocation } from 'react-router-dom'

import { ErrorBoundary } from 'components/ErrorBoundary'
import { PageContextProvider } from 'context/PageContext'
import {
    changeOrganization,
    changeOrganizationGroup,
} from 'features/Auth/authService'
import { RumViewTracker } from 'features/Monitoring/Datadog/RumViewTracker'
import { useCurrentOrganization, useCurrentOrganizationGroup } from 'hooks'
import { getPath } from 'routing/pagePaths'
import { PageId } from 'routing/pages'
import { OrganizationLockedPage } from 'views/OrganizationLockedPage'

type Props = {
    pageName: PageId
    restricted?: boolean
}

function PageRoute({
    pageName,
    restricted = false,
    ...rest
}: Props & OutletProps): ReactElement {
    const location = useLocation()
    const organization = useCurrentOrganization()
    const organizationGroup = useCurrentOrganizationGroup()

    const params: {
        orgId?: string
        orgGroupId?: string
        redirect?: string
        filters?: any
        resources?: any
        tab?: string
    } = queryString.parse(location.search)

    if (
        params.orgId ||
        params.orgGroupId ||
        params.filters ||
        params.redirect ||
        params.resources
    ) {
        try {
            params.filters = params.filters ? JSON.parse(params.filters) : {}
        } catch (e) {
            console.error('Error parsing filters', e)
            params.filters = {}
        }
        try {
            params.resources = params.resources
                ? JSON.parse(params.resources)
                : {}
        } catch (e) {
            console.error('Error parsing resources', e)
            params.resources = {}
        }
        if (params.orgGroupId || organizationGroup) {
            changeOrganizationGroup({
                ...params,
                organizationGroupId: (params.orgGroupId ??
                    organizationGroup?.id) as string,
                organizationId: (params.orgId ?? organization?.id) as string,
            })
        } else {
            changeOrganization({
                ...params,
                organizationId: (params.orgId ?? organization?.id) as string,
            })
        }
    }

    if (
        organization &&
        (organization.locked === true || organization.enabled === false) &&
        !location.pathname.startsWith(getPath('settingsPage'))
    ) {
        return <OrganizationLockedPage organization={organization} />
    }

    if (restricted) {
        return <Navigate to="/" />
    }

    return (
        <ErrorBoundary key={pageName}>
            <PageContextProvider pageName={pageName}>
                <RumViewTracker />
                <Outlet {...rest} />
            </PageContextProvider>
        </ErrorBoundary>
    )
}

export default PageRoute
